import React, { useState, useRef, useEffect } from 'react';
import {
    Chip,
    Box,
    Tooltip,
    Fade,
    Paper,
    Typography,
    ClickAwayListener,
    TextField,
    InputAdornment,
    List,
    ListItem,
    ListItemText,
    Avatar,
    CircularProgress,
    Snackbar,
    Alert,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import BlockIcon from '@mui/icons-material/Block';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import SearchIcon from '@mui/icons-material/Search';
import theme from '../../../theme';
import { getStatusChipProps } from './constants';
import { useDeficiencies, getAssignmentMetadata } from './DeficiencyContext';
import cookie from 'js-cookie';

// Helper function to get current user's business ID from cookies
const getCurrentUserBusinessId = () => {
    const businessId = cookie.get('businessId');
    if (businessId) return businessId;
    return null;
};

// Only enhance certain statuses that have quick actions
const isPendingStatus = (status) => {
    // These are the statuses that can have quick actions
    return status === 'Submitted' || status === 'Accepted' || status === 'Work Completed';
};

// A component that shows status and quick actions in a single element
// NOTE: This component should only be rendered for internal managers (INTERNAL_PM, INTERNAL_SUPER, INTERNAL_ADMIN)
// The parent component (DeficiencyList) handles role-checking before rendering this component
const QuickActionButton = ({ status, onAction, deficiencyId }) => {
    const [showActions, setShowActions] = useState(false);
    const [position, setPosition] = useState({ top: 0, left: 0 });
    const [searchValue, setSearchValue] = useState('');
    const [filteredBusinesses, setFilteredBusinesses] = useState([]);
    const [currentUserBusiness, setCurrentUserBusiness] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const buttonRef = useRef(null);
    const dropdownRef = useRef(null);
    const { state, updateDeficiency } = useDeficiencies();
    const { businesses, businessesLoading } = state;

    // Identify the current user's business once when we have the businesses list
    // The current user's business ID never changes (it's from authentication)
    useEffect(() => {
        if (businesses && businesses.length > 0 && !currentUserBusiness) {
            const currentBusinessId = getCurrentUserBusinessId();

            if (currentBusinessId) {
                const foundBusiness = businesses.find(
                    (business) => business.businessId === currentBusinessId
                );
                setCurrentUserBusiness(foundBusiness);
            }
        }
    }, [businesses, currentUserBusiness]);

    // Filter businesses based on search input
    useEffect(() => {
        if (searchValue.trim() === '') {
            setFilteredBusinesses([]);
            return;
        }

        const searchLower = searchValue.toLowerCase();
        const filtered = businesses.filter(
            (business) =>
                (business.businessName &&
                    business.businessName.toLowerCase().includes(searchLower)) ||
                (business.subscriptionType &&
                    business.subscriptionType.toString().toLowerCase().includes(searchLower)) ||
                (business.subscriptionType === null && 'external'.includes(searchLower)) ||
                (business.subscriptionType === null && 'subcontractor'.includes(searchLower))
        );
        setFilteredBusinesses(filtered);
    }, [searchValue, businesses]);

    // Direct API update function
    const updateDeficiencyWithAPI = async (data) => {
        try {
            setLoading(true);
            setError(null);
            setSuccess(false); // Reset success state first

            // The context's updateDeficiency function now handles getting the full deficiency
            // and merging in our changes, so we can just pass the changes directly
            const result = await updateDeficiency(deficiencyId, data);

            setSuccess(true);

            // Create a clean, professional notification at the bottom of the screen
            //TODO: move this to a global notification component
            //TODO: Make this dynamic size and position and maybe fix the styling
            const notification = document.createElement('div');
            notification.style.position = 'fixed';
            notification.style.bottom = '24px';
            notification.style.left = '50%';
            notification.style.transform = 'translateX(-50%)';
            notification.style.backgroundColor = '#fff';
            notification.style.color = '#2e7d32'; // Material-UI success color
            notification.style.padding = '10px 16px';
            notification.style.borderRadius = '4px';
            notification.style.boxShadow = '0 3px 10px rgba(0,0,0,0.15)';
            notification.style.zIndex = '9999';
            notification.style.fontFamily = 'Roboto, Arial, sans-serif';
            notification.style.display = 'flex';
            notification.style.alignItems = 'center';
            notification.style.minWidth = '200px';
            notification.style.maxWidth = '400px';
            notification.style.fontSize = '14px';

            // Add a success icon
            const iconSpan = document.createElement('span');
            iconSpan.innerHTML = '✓';
            iconSpan.style.marginRight = '12px';
            iconSpan.style.color = '#2e7d32';
            iconSpan.style.fontWeight = 'bold';
            iconSpan.style.fontSize = '18px';

            const textSpan = document.createElement('span');
            textSpan.textContent = 'Deficiency updated successfully';
            textSpan.style.fontWeight = '500';

            notification.appendChild(iconSpan);
            notification.appendChild(textSpan);
            document.body.appendChild(notification);

            // Remove the notification after 3 seconds
            setTimeout(() => {
                notification.style.opacity = '0';
                notification.style.transition = 'opacity 0.3s ease';
                setTimeout(() => {
                    if (document.body.contains(notification)) {
                        document.body.removeChild(notification);
                    }
                }, 300);
            }, 3000);

            // Notify parent component about the update if needed
            if (onAction) {
                onAction('update-success', data);
            }

            return result;
        } catch (err) {
            // Set error state instead of logging to console
            setError(err.message || 'Failed to update deficiency');

            // Create error notification with same styling
            const errorNotification = document.createElement('div');
            errorNotification.style.position = 'fixed';
            errorNotification.style.bottom = '24px';
            errorNotification.style.left = '50%';
            errorNotification.style.transform = 'translateX(-50%)';
            errorNotification.style.backgroundColor = '#fff';
            errorNotification.style.color = '#d32f2f'; // Material-UI error color
            errorNotification.style.padding = '10px 16px';
            errorNotification.style.borderRadius = '4px';
            errorNotification.style.boxShadow = '0 3px 10px rgba(0,0,0,0.15)';
            errorNotification.style.zIndex = '9999';
            errorNotification.style.fontFamily = 'Roboto, Arial, sans-serif';
            errorNotification.style.display = 'flex';
            errorNotification.style.alignItems = 'center';
            errorNotification.style.minWidth = '200px';
            errorNotification.style.maxWidth = '400px';
            errorNotification.style.fontSize = '14px';

            // Add an error icon
            const iconSpan = document.createElement('span');
            iconSpan.innerHTML = '✕';
            iconSpan.style.marginRight = '12px';
            iconSpan.style.color = '#d32f2f';
            iconSpan.style.fontWeight = 'bold';
            iconSpan.style.fontSize = '18px';

            const textSpan = document.createElement('span');
            textSpan.textContent = `Error: ${err.message || 'Failed to update deficiency'}`;
            textSpan.style.fontWeight = '500';

            errorNotification.appendChild(iconSpan);
            errorNotification.appendChild(textSpan);
            document.body.appendChild(errorNotification);

            // Remove the notification after 5 seconds
            setTimeout(() => {
                errorNotification.style.opacity = '0';
                errorNotification.style.transition = 'opacity 0.3s ease';
                setTimeout(() => {
                    if (document.body.contains(errorNotification)) {
                        document.body.removeChild(errorNotification);
                    }
                }, 300);
            }, 5000);

            return null;
        } finally {
            setLoading(false);
            // Close dropdown after update attempt
            handleClose();
        }
    };

    const updatePosition = () => {
        if (buttonRef.current && showActions) {
            const rect = buttonRef.current.getBoundingClientRect();
            const dropdownHeight = dropdownRef.current?.offsetHeight || 400;
            const dropdownWidth = dropdownRef.current?.offsetWidth || 300;

            // Check if there's enough space below
            let top = rect.bottom + 4;
            let left = rect.left;

            // If dropdown would go off bottom of screen, position above button instead
            if (top + dropdownHeight > window.innerHeight) {
                top = rect.top - dropdownHeight - 4;
            }

            // If dropdown would go off right of screen, align right edge with button
            if (left + dropdownWidth > window.innerWidth) {
                left = rect.right - dropdownWidth;
            }

            // Ensure dropdown doesn't go off left side
            left = Math.max(8, left);

            setPosition({ top, left });
        }
    };

    useEffect(() => {
        if (showActions) {
            // Initial position update
            setTimeout(updatePosition, 0);

            // Add event listeners
            window.addEventListener('scroll', updatePosition, true);
            window.addEventListener('resize', updatePosition);
        }
        return () => {
            window.removeEventListener('scroll', updatePosition, true);
            window.removeEventListener('resize', updatePosition);
        };
    }, [showActions]);

    // Only show quick actions for statuses that have them
    const isPending = isPendingStatus(status);

    if (!isPending) return null;

    // This is the function that gets the quick actions for the status
    // Action names are explicit to prevent ambiguity:
    // - 'reject_terminal' for terminal rejection (SUBMITTED quick-reject)
    // - 'reject_work' for work rejection/bounce-back (WORK_COMPLETED)
    // NOTE: Role checking is handled by parent component - this only renders for internal managers
    const getQuickActions = () => {
        switch (status) {
            case 'Submitted':
                return [
                    {
                        action: 'accept',
                        label: 'Accept',
                        icon: <CheckIcon fontSize="small" sx={{ mr: 0.5 }} />,
                        color: theme.palette.status.success,
                    },
                    {
                        action: 'reject_terminal',
                        label: 'Reject',
                        icon: <BlockIcon fontSize="small" sx={{ mr: 0.5 }} />,
                        color: theme.palette.status.error,
                    },
                ];
            case 'Accepted':
                return [
                    {
                        action: 'assign-self',
                        label: 'Assign to My Business',
                        icon: <AssignmentIndIcon fontSize="small" sx={{ mr: 0.5 }} />,
                        color: theme.palette.secondary.main,
                    },
                ];
            case 'Work Completed':
                return [
                    {
                        action: 'sign-off',
                        label: 'Sign Off',
                        icon: <CheckIcon fontSize="small" sx={{ mr: 0.5 }} />,
                        color: theme.palette.status.success,
                    },
                    {
                        action: 'reject_work',
                        label: 'Reject Work',
                        icon: <BlockIcon fontSize="small" sx={{ mr: 0.5 }} />,
                        color: theme.palette.status.error,
                    },
                ];
            default:
                return [];
        }
    };

    const actions = getQuickActions();

    const handleOpen = (event) => {
        // Prevent event from bubbling up to parent elements
        event.stopPropagation();
        setShowActions(true);
    };

    const handleClose = () => {
        setShowActions(false);
    };

    const handleAction = (action, event) => {
        // Prevent the event from bubbling up to parent elements
        if (event) {
            event.stopPropagation();
            event.preventDefault();
        }
        // Get current user business ID for metadata
        const currentBusinessId = getCurrentUserBusinessId();

        if (action === 'accept') {
            // Update via API - status metadata is handled by the backend
            updateDeficiencyWithAPI({
                status: 'Accepted',
            });
        } else if (action === 'reject_terminal') {
            // Terminal rejection from SUBMITTED - bubble up with explicit action type
            onAction('reject_terminal');
            handleClose();
        } else if (action === 'reject_work') {
            // Work rejection/bounce-back from WORK_COMPLETED - bubble up with explicit action type
            onAction('reject_work');
            handleClose();
        } else if (action === 'sign-off') {
            // Update via API - status metadata is handled by the backend
            updateDeficiencyWithAPI({
                status: 'Internally Signed Off',
            });
        } else if (action === 'assign-self') {
            // If current business not found, use a fallback
            if (!currentUserBusiness) {
                // Business info not available, proceed with simple action
                // without detailed business metadata
                onAction(action);
            } else {
                // Use the actual business data and proper metadata
                updateDeficiencyWithAPI({
                    status: 'Assigned',
                    ...getAssignmentMetadata(
                        currentBusinessId,
                        currentUserBusiness,
                        status,
                        currentBusinessId
                    ),
                });
            }
        } else {
            // For other actions, use the callback
            onAction(action);
        }
    };

    const handleSearch = (event) => {
        if (event.key === 'Enter' && searchValue.trim()) {
            // Stop propagation to prevent sidebar opening
            event.stopPropagation();
            onAction('assign', { status: 'In Progress', assignee: searchValue.trim() });
            setSearchValue('');
            handleClose();
        }
    };

    const handleBusinessSelect = (business, event) => {
        // Stop propagation to prevent sidebar opening
        event.stopPropagation();

        // Update via API - both status and assignee
        updateDeficiencyWithAPI({
            status: 'Assigned',
            assignedToBusinessId: business.businessId,
            assignedToBusinessName: business.businessName,
        });

        setSearchValue('');
        setFilteredBusinesses([]);
    };

    // Get the status styles from the constants file
    const chipProps = getStatusChipProps(status);

    const handleClickAway = (event) => {
        const isWithinDropdown = dropdownRef.current?.contains(event.target);
        const isWithinButton = buttonRef.current?.contains(event.target);
        const isInput = event.target.tagName.toLowerCase() === 'input';

        // Don't close if clicking within components or if it's an input event
        if (isWithinDropdown || isWithinButton || isInput) {
            return;
        }
        handleClose();
    };

    // Handle closing the success message
    const handleSuccessClose = () => {
        setSuccess(false);
    };

    // Handle closing the error message
    const handleErrorClose = () => {
        setError(null);
    };

    return (
        <>
            <ClickAwayListener onClickAway={handleClickAway} mouseEvent="onMouseDown">
                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                    <Box ref={buttonRef}>
                        <Tooltip arrow>
                            <Chip
                                label={
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontWeight: 600,
                                                    fontSize: '0.75rem',
                                                    lineHeight: 1.2,
                                                    letterSpacing: '0.01em',
                                                }}
                                            >
                                                {status}
                                            </Typography>
                                            <ArrowDropDownIcon
                                                fontSize="small"
                                                sx={{
                                                    ml: 0.3,
                                                    transition: 'transform 0.2s',
                                                    transform: showActions
                                                        ? 'rotate(180deg)'
                                                        : 'rotate(0)',
                                                    width: '16px',
                                                    height: '16px',
                                                }}
                                            />
                                        </Box>
                                        {chipProps.pendingDescription && (
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    fontSize: '0.625rem',
                                                    lineHeight: 1.1,
                                                    opacity: 0.85,
                                                    width: '100%',
                                                    textAlign: 'center',
                                                    mt: 0.1,
                                                }}
                                            >
                                                {chipProps.pendingDescription}
                                            </Typography>
                                        )}
                                    </Box>
                                }
                                size="small"
                                onClick={handleOpen}
                                sx={{
                                    ...chipProps.sx,
                                    cursor: 'pointer',
                                    height: 'auto',
                                    py: 0.3,
                                    '& .MuiChip-label': {
                                        padding: '2px 10px',
                                        display: 'block',
                                        width: '100%',
                                    },
                                    borderRadius: '16px',
                                    '&:hover': {
                                        opacity: 0.9,
                                        boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                                    },
                                    transition: 'all 0.2s ease',
                                    fontFamily: theme.typography.fontFamily,
                                }}
                            />
                        </Tooltip>
                    </Box>

                    <Fade in={showActions}>
                        <Paper
                            elevation={3}
                            ref={dropdownRef}
                            sx={{
                                display: showActions ? 'block' : 'none',
                                position: 'fixed',
                                zIndex: 1300,
                                borderRadius: 1,
                                overflow: 'hidden',
                                width: 'auto',
                                minWidth: 300,
                                maxHeight: 400,
                                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                            }}
                            style={{
                                top: position.top,
                                left: position.left,
                            }}
                            onClick={(e) => e.stopPropagation()} // Prevent clicks from reaching parent elements
                        >
                            {status === 'Accepted' && (
                                <>
                                    <Box
                                        sx={{
                                            p: 1,
                                            borderBottom: `1px solid ${theme.palette.grey[100]}`,
                                            backgroundColor: theme.palette.background.paper,
                                        }}
                                    >
                                        <TextField
                                            size="small"
                                            fullWidth
                                            placeholder="Search for business..."
                                            value={searchValue}
                                            onChange={(e) => setSearchValue(e.target.value)}
                                            onKeyDown={handleSearch}
                                            onClick={(e) => e.stopPropagation()} // Prevent clicks from reaching parent elements
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <SearchIcon
                                                            sx={{
                                                                color: theme.palette.grey[400],
                                                                fontSize: '1.2rem',
                                                            }}
                                                        />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    backgroundColor: theme.palette.background.paper,
                                                    '& fieldset': {
                                                        borderColor: theme.palette.grey[200],
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: theme.palette.grey[300],
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: theme.palette.primary.main,
                                                    },
                                                },
                                            }}
                                        />
                                    </Box>
                                    {businessesLoading || loading ? (
                                        <Box
                                            sx={{ display: 'flex', justifyContent: 'center', p: 2 }}
                                        >
                                            <CircularProgress size={24} />
                                        </Box>
                                    ) : filteredBusinesses.length > 0 ? (
                                        <List
                                            sx={{
                                                maxHeight: 200,
                                                overflow: 'auto',
                                                borderBottom: `1px solid ${theme.palette.grey[100]}`,
                                                backgroundColor: theme.palette.background.paper,
                                            }}
                                        >
                                            {filteredBusinesses.map((business) => (
                                                <ListItem
                                                    key={business.businessId}
                                                    onClick={(e) =>
                                                        handleBusinessSelect(business, e)
                                                    }
                                                    sx={{
                                                        cursor: 'pointer',
                                                        '&:hover': {
                                                            backgroundColor:
                                                                theme.palette.action.hover,
                                                        },
                                                    }}
                                                >
                                                    <Avatar
                                                        sx={{
                                                            width: 24,
                                                            height: 24,
                                                            fontSize: '0.75rem',
                                                            mr: 1,
                                                            bgcolor: theme.palette.primary.main,
                                                        }}
                                                    >
                                                        {business.businessName &&
                                                        business.businessName[0]
                                                            ? business.businessName[0]
                                                            : '?'}
                                                    </Avatar>
                                                    <ListItemText
                                                        primary={
                                                            <Typography
                                                                variant="body2"
                                                                fontWeight={500}
                                                            >
                                                                {business.businessName}
                                                            </Typography>
                                                        }
                                                        primaryTypographyProps={{
                                                            variant: 'body2',
                                                        }}
                                                    />
                                                </ListItem>
                                            ))}
                                        </List>
                                    ) : searchValue.trim() ? (
                                        <Box sx={{ p: 2, textAlign: 'center' }}>
                                            <Typography variant="body2" color="text.secondary">
                                                No businesses found
                                            </Typography>
                                        </Box>
                                    ) : null}
                                </>
                            )}
                            <Box sx={{ backgroundColor: theme.palette.background.paper }}>
                                {actions.map((action) => (
                                    <Box
                                        key={action.action}
                                        onClick={(e) => handleAction(action.action, e)}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            px: 2,
                                            py: 1,
                                            cursor: 'pointer',
                                            '&:hover': {
                                                backgroundColor: `${action.color}15`,
                                            },
                                            borderBottom: `1px solid ${theme.palette.grey[100]}`,
                                            '&:last-child': {
                                                borderBottom: 'none',
                                            },
                                        }}
                                    >
                                        {action.icon}
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: action.color,
                                                fontWeight: theme.typography.fontWeights.medium,
                                            }}
                                        >
                                            {action.label}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Paper>
                    </Fade>
                </Box>
            </ClickAwayListener>

            {/* Success notification */}
            <Snackbar
                open={success}
                autoHideDuration={1000}
                onClose={handleSuccessClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                sx={{ zIndex: 9999 }}
            >
                <Alert
                    onClose={handleSuccessClose}
                    severity="success"
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    Deficiency updated successfully
                </Alert>
            </Snackbar>

            {/* Error notification */}
            <Snackbar
                open={!!error}
                autoHideDuration={5000}
                onClose={handleErrorClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                sx={{ zIndex: 9999 }}
            >
                <Alert
                    onClose={handleErrorClose}
                    severity="error"
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {error}
                </Alert>
            </Snackbar>
        </>
    );
};

export default QuickActionButton;
